import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import Add from '@mui/icons-material/Add';
import OpenInFull from '@mui/icons-material/OpenInFull';
import Remove from '@mui/icons-material/Remove';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { ModelError_Response } from '@treadinc/horizon-api-spec';
import { AxiosError, isAxiosError } from 'axios';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import {
  CSSProperties,
  PointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { TREAD_AGENT_ATTACHMENT_CONFIG } from '~hooks/useComposerAttachments/composerAttachmentConfigs';
import useComposerAttachments from '~hooks/useComposerAttachments/useComposerAttachments';
import useTreadAgent from '~hooks/useTreadAgent/useTreadAgent';
import useTreadAgentConversationQuery from '~queries/useTreadAgentConversationQuery';
import useTreadAgentSkillsQuery from '~queries/useTreadAgentSkillsQuery';
import { useStores } from '~store';
import ChatInput from '~tread-agent/ChatInput';
import MessageList from '~tread-agent/MessageList';
import TreadAgentExpandedView, {
  CHAT_CONTENT_MAX_WIDTH_IN_PX,
} from '~tread-agent/TreadAgentExpandedView';
import { ExpandedTabStripRenderer } from '~tread-agent/TreadAgentTabs';
import { TREAD_AGENT_STARTER_PROMPT_KEYS } from '~tread-agent/treadAgentUtils';
import {
  PanelPosition,
  PanelResizeEdge,
  PanelSize,
} from '~utils/hooks/useDraggableResizablePanel';
import { usePrevious } from '~utils/hooks/usePrevious';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

// Compact panel dimensions follow the Tread Agent design (Linear-style assistant dock).
export const TREAD_AGENT_PANEL_DEFAULT_SIZE_IN_PX: PanelSize = {
  height: 600,
  width: 400,
};
export const TREAD_AGENT_PANEL_MIN_SIZE_IN_PX: PanelSize = { height: 360, width: 320 };
// Keeps the panel clear of the docked bar: the 42px bar height (TREAD_AGENT_DOCK_HEIGHT_IN_PX
// in TreadAgentDock) plus an 8px visual gap.
const DOCK_CLEARANCE_IN_PX = 50;

const FRESH_CHAT_COMPOSER_MIN_ROWS = 2;

const RESIZE_EDGE_THICKNESS = '6px';
const RESIZE_CORNER_SIZE = '14px';

const RESIZE_HANDLES: { edge: PanelResizeEdge; style: CSSProperties }[] = [
  {
    edge: 'n',
    style: {
      cursor: 'ns-resize',
      height: RESIZE_EDGE_THICKNESS,
      left: RESIZE_CORNER_SIZE,
      right: RESIZE_CORNER_SIZE,
      top: 0,
    },
  },
  {
    edge: 's',
    style: {
      bottom: 0,
      cursor: 'ns-resize',
      height: RESIZE_EDGE_THICKNESS,
      left: RESIZE_CORNER_SIZE,
      right: RESIZE_CORNER_SIZE,
    },
  },
  {
    edge: 'w',
    style: {
      bottom: RESIZE_CORNER_SIZE,
      cursor: 'ew-resize',
      left: 0,
      top: RESIZE_CORNER_SIZE,
      width: RESIZE_EDGE_THICKNESS,
    },
  },
  {
    edge: 'e',
    style: {
      bottom: RESIZE_CORNER_SIZE,
      cursor: 'ew-resize',
      right: 0,
      top: RESIZE_CORNER_SIZE,
      width: RESIZE_EDGE_THICKNESS,
    },
  },
  {
    edge: 'nw',
    style: {
      cursor: 'nwse-resize',
      height: RESIZE_CORNER_SIZE,
      left: 0,
      top: 0,
      width: RESIZE_CORNER_SIZE,
      zIndex: 2,
    },
  },
  {
    edge: 'ne',
    style: {
      cursor: 'nesw-resize',
      height: RESIZE_CORNER_SIZE,
      right: 0,
      top: 0,
      width: RESIZE_CORNER_SIZE,
      zIndex: 2,
    },
  },
  {
    edge: 'sw',
    style: {
      bottom: 0,
      cursor: 'nesw-resize',
      height: RESIZE_CORNER_SIZE,
      left: 0,
      width: RESIZE_CORNER_SIZE,
      zIndex: 2,
    },
  },
  {
    edge: 'se',
    style: {
      bottom: 0,
      cursor: 'nwse-resize',
      height: RESIZE_CORNER_SIZE,
      right: 0,
      width: RESIZE_CORNER_SIZE,
      zIndex: 2,
    },
  },
];

// The compact docked card vs the full-size overlay. The panel owns all composer state, so
// toggling between the two keeps the draft, attachments, and open conversation intact.
export type TreadAgentViewMode = 'compact' | 'expanded';

interface TreadAgentPanelProps {
  expandedTabStrip: ExpandedTabStripRenderer;
  isMinimized: boolean;
  onClose: () => void;
  onMinimize: () => void;
  onNewChat: () => void;
  onResizeStart: (edge: PanelResizeEdge) => (event: PointerEvent<HTMLElement>) => void;
  onSelectConversation: (conversationId: string) => void;
  // Flip between the compact card and the expanded overlay (see TreadAgentDock).
  onToggleViewMode: () => void;
  position: PanelPosition | null;
  sizeInPx: PanelSize;
  viewMode: TreadAgentViewMode;
}

const TreadAgentPanel = observer((props: TreadAgentPanelProps) => {
  const {
    expandedTabStrip,
    isMinimized,
    onClose,
    onMinimize,
    onNewChat,
    onResizeStart,
    onSelectConversation,
    onToggleViewMode,
    position,
    sizeInPx,
    viewMode,
  } = props;
  const theme = useTheme();
  const { treadAgentStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const { isDragging, listeners, setNodeRef, transform } = useDraggable({
    id: 'tread-agent-panel',
  });

  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendErrorMessage, setSendErrorMessage] = useState<string | null>(null);
  const {
    addFiles,
    attachments,
    blocksSend: attachmentsBlockSend,
    clearAttachments,
    clearError: clearAttachmentError,
    error: attachmentError,
    isAtAttachmentLimit,
    removeAttachment,
    uploadedSignedIds,
  } = useComposerAttachments(TREAD_AGENT_ATTACHMENT_CONFIG);

  const activeConversationId = treadAgentStore.activeConversationId;
  const { conversation: activeConversation } = useTreadAgentConversationQuery(
    companyId,
    activeConversationId,
  );
  const previousConversationId = usePrevious(activeConversationId);
  const skillCommandForNewChatRef = useRef<string | null>(null);
  useEffect(() => {
    const isFirstRender = previousConversationId === undefined;
    const conversationJustCreatedBySend = previousConversationId === null && isSending;

    if (
      isFirstRender ||
      previousConversationId === activeConversationId ||
      conversationJustCreatedBySend
    ) {
      return;
    }

    setInputValue(skillCommandForNewChatRef.current ?? '');
    skillCommandForNewChatRef.current = null;
    setSendErrorMessage(null);
    clearAttachments();
  }, [activeConversationId, clearAttachments, isSending, previousConversationId]);

  const { sendMessage, stopTurn } = useTreadAgent();
  const { skills } = useTreadAgentSkillsQuery(companyId);

  const isSendBlocked = isSending || treadAgentStore.isTurnInProgress;

  const sendToAgent = useCallback(
    async (content: string, signedIds: string[]) => {
      setIsSending(true);
      setSendErrorMessage(null);

      try {
        // Resolves undefined without throwing when creating a new conversation fails.
        return await sendMessage(companyId, content, signedIds);
      } catch (error) {
        // A 422 means the backend rejected an attachment; show its reason inline.
        if (isAxiosError(error)) {
          const axiosError = error as AxiosError<ModelError_Response>;
          const firstError = axiosError.response?.data.error.errors?.[0]?.message;
          setSendErrorMessage(
            firstError ?? t('error_messages.tread_agent.failed_to_send_message'),
          );
        }
      } finally {
        setIsSending(false);
      }
    },
    [companyId, sendMessage],
  );

  const sendComposerContent = useCallback(
    async (content: string) => {
      const trimmedContent = content.trim();
      const hasNothingToSend = !trimmedContent && uploadedSignedIds.length === 0;

      if (hasNothingToSend || attachmentsBlockSend || isSendBlocked) {
        return;
      }

      const conversationId = await sendToAgent(trimmedContent, uploadedSignedIds);

      if (conversationId) {
        setInputValue('');
        clearAttachments();
      }
    },
    [
      attachmentsBlockSend,
      clearAttachments,
      isSendBlocked,
      sendToAgent,
      uploadedSignedIds,
    ],
  );

  const sendRequestedPrompt = useCallback(
    async (prompt: string) => {
      const conversationId = await sendToAgent(prompt.trim(), []);

      if (!conversationId) {
        setInputValue((draft) => draft || prompt);
      }
    },
    [sendToAgent],
  );

  const sendablePendingPrompt = isSendBlocked ? null : treadAgentStore.pendingPrompt;
  useEffect(() => {
    if (sendablePendingPrompt) {
      treadAgentStore.clearPendingPrompt();
      void sendRequestedPrompt(sendablePendingPrompt);
    }
  }, [sendablePendingPrompt, sendRequestedPrompt]);

  const handleValueChange = useCallback(
    (value: string) => {
      setInputValue(value);
      setSendErrorMessage(null);
      clearAttachmentError();
    },
    [clearAttachmentError],
  );

  const handleUseSkill = useCallback(
    (skillName: string) => {
      const skillCommand = `/${skillName} `;

      if (activeConversationId === null) {
        handleValueChange(skillCommand);

        return;
      }

      skillCommandForNewChatRef.current = skillCommand;
      onNewChat();
    },
    [activeConversationId, handleValueChange, onNewChat],
  );

  const handleSend = useCallback(
    () => sendComposerContent(inputValue),
    [sendComposerContent, inputValue],
  );

  const handleStop = useCallback(async () => {
    if (!activeConversationId) {
      return;
    }

    try {
      await stopTurn(companyId, activeConversationId);
    } catch {
      // stopTurn already reported the failure and put the control back.
    }
  }, [activeConversationId, companyId, stopTurn]);

  const isFreshExpandedChat = viewMode === 'expanded' && activeConversationId === null;
  const emptyState =
    activeConversationId === null ? (
      <Box
        sx={
          isFreshExpandedChat
            ? { display: 'flex', flexDirection: 'column', gap: 0.5, mt: 2, mx: 1.5 }
            : {
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                gap: 0.75,
                justifyContent: 'center',
                overflowY: 'auto',
                px: 2,
                py: 3,
              }
        }
      >
        {TREAD_AGENT_STARTER_PROMPT_KEYS.map((promptKey) => (
          <ButtonBase
            key={promptKey}
            disabled={isSending}
            onClick={() => sendComposerContent(t(promptKey))}
            sx={{
              bgcolor: theme.brandV2.colors.treadGray9,
              borderRadius: theme.brandV2.borderRadius,
              display: 'flex',
              justifyContent: 'flex-start',
              px: 1.5,
              py: 1.25,
              textAlign: 'left',
              '&:hover': { bgcolor: theme.brandV2.colors.treadGray8 },
            }}
          >
            <Typography variant="body2">{t(promptKey)}</Typography>
          </ButtonBase>
        ))}
      </Box>
    ) : undefined;

  const title = activeConversation?.title || t('tread_agent.title');

  // Both surfaces share the same transcript and composer instances so the draft, uploads, and
  // scroll position carry over when toggling between compact and expanded.
  const messageList = (
    <MessageList
      contentMaxWidthInPx={
        viewMode === 'expanded' ? CHAT_CONTENT_MAX_WIDTH_IN_PX : undefined
      }
      emptyState={emptyState}
    />
  );
  const chatInput = (
    <ChatInput
      attachments={attachments}
      errorMessage={attachmentError ?? sendErrorMessage}
      attachmentsBlockSend={attachmentsBlockSend}
      isDisabled={treadAgentStore.isTurnInProgress || isSending}
      isStopRequested={treadAgentStore.isStopRequested}
      isTurnInProgress={treadAgentStore.isTurnInProgress}
      minRows={isFreshExpandedChat ? FRESH_CHAT_COMPOSER_MIN_ROWS : undefined}
      isAtAttachmentLimit={isAtAttachmentLimit}
      onAddFiles={addFiles}
      onRemoveAttachment={removeAttachment}
      onSend={handleSend}
      onStop={handleStop}
      onValueChange={handleValueChange}
      skills={skills}
      value={inputValue}
    />
  );

  if (viewMode === 'expanded') {
    return (
      <TreadAgentExpandedView
        activeConversationId={activeConversationId}
        chatInput={chatInput}
        isFreshChat={isFreshExpandedChat}
        messageList={messageList}
        onClose={onClose}
        onCollapse={onToggleViewMode}
        onNewChat={onNewChat}
        onSelectConversation={onSelectConversation}
        onUseSkill={handleUseSkill}
        tabStrip={expandedTabStrip}
      />
    );
  }

  return (
    <Paper
      data-test-id="tread-agent-panel"
      elevation={0}
      ref={setNodeRef}
      sx={{
        border: `1px solid ${theme.brandV2.colors.treadGray7}`,
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
        display: 'flex',
        flexDirection: 'column',
        height: `${sizeInPx.height}px`,
        overflow: 'hidden',
        position: 'fixed',
        transform: CSS.Translate.toString(transform),
        width: `${sizeInPx.width}px`,
        // visibility, not display: none, so the transcript keeps its scroll offset.
        ...(isMinimized ? { pointerEvents: 'none', visibility: 'hidden' } : {}),
        ...(position
          ? { left: `${position.left}px`, top: `${position.top}px` }
          : { bottom: `${DOCK_CLEARANCE_IN_PX}px`, right: '16px' }),
        // Above page content and the dock strip, below blocking MUI dialogs.
        zIndex: theme.zIndex.modal - 1,
      }}
    >
      <Box
        {...listeners}
        data-test-id="tread-agent-panel-header"
        sx={{
          alignItems: 'center',
          borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
          cursor: isDragging ? 'grabbing' : 'grab',
          display: 'flex',
          gap: 0.5,
          px: 1.5,
          py: 1,
          // Hand the gesture to the drag sensor instead of the browser's touch panning,
          // which would otherwise cancel the drag on a touchscreen.
          touchAction: 'none',
          userSelect: 'none',
        }}
      >
        <Typography noWrap sx={{ flexGrow: 1, fontSize: '14px', fontWeight: 600 }}>
          {title}
        </Typography>

        <SmallButton
          color="brandV2Black"
          data-no-dnd="true"
          onClick={onNewChat}
          startIcon={<Add sx={{ fontSize: 16 }} />}
          sx={{
            borderColor: 'divider',
            color: 'text.primary',
            flexShrink: 0,
            '&.MuiButtonBase-root': { px: 1 },
            '& .MuiButton-startIcon': { ml: 0, mr: 0.25 },
          }}
          variant="outlined"
        >
          {t('tread_agent.new')}
        </SmallButton>

        <IconButton
          aria-label={t('tread_agent.expand')}
          data-no-dnd="true"
          onClick={onToggleViewMode}
          size="small"
        >
          <OpenInFull sx={{ fontSize: 16 }} />
        </IconButton>

        <IconButton
          aria-label={t('tread_agent.minimize')}
          data-no-dnd="true"
          onClick={onMinimize}
          size="small"
        >
          <Remove sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {messageList}

      {chatInput}

      {RESIZE_HANDLES.map(({ edge, style }) => (
        <Box
          key={edge}
          data-test-id={`tread-agent-panel-resize-${edge}`}
          onPointerDown={onResizeStart(edge)}
          sx={{ position: 'absolute', touchAction: 'none', zIndex: 1, ...style }}
        />
      ))}
    </Paper>
  );
});

export default TreadAgentPanel;
